import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Calendar, Camera, MessageCircle, Clock, Star, Cloud, Eye, ChevronDown, ChevronUp } from 'lucide-react';

// Demo component to show how the visit details functionality works
export default function DemoVisitedSpots() {
  const [expandedSpots, setExpandedSpots] = useState({});

  // Sample data based on our API response
  const demoData = [
    {
      spot: {
        id: "demo1",
        name: "Lac de montagne paisible",
        title: "Lac de montagne paisible",
        location: "Alpes françaises"
      },
      visitCount: 2,
      firstVisitDate: new Date("2025-08-15"),
      lastVisitDate: new Date("2025-08-21"),
      visits: [
        {
          id: "visit1",
          visit_date: "2025-08-21T06:00:00",
          description: "Test visite depuis API",
          rating: 5,
          weather: "sunny",
          photos: []
        },
        {
          id: "visit2", 
          visit_date: "2025-08-15T14:30:00",
          description: "Deuxième visite avec des amis - magnifique coucher de soleil !",
          rating: 4,
          weather: "partly-cloudy",
          photos: []
        }
      ]
    }
  ];

  const formatCompactDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatVisitDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const toggleSpotExpansion = (spotId) => {
    setExpandedSpots(prev => ({
      ...prev,
      [spotId]: !prev[spotId]
    }));
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Démonstration - Mes visites (2 visites sur 1 spot)
          </CardTitle>
        </CardHeader>
      </Card>

      {demoData.map((spotVisit) => {
        const spot = spotVisit.spot;
        const isExpanded = expandedSpots[spot.id];
        
        return (
          <Card key={spot.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Spot Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {spot.name}
                    </h3>
                    
                    {spot.location && (
                      <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                        <MapPin className="h-4 w-4" />
                        <span>{spot.location}</span>
                      </div>
                    )}

                    {/* Visit Summary Line */}
                    <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-3 border border-blue-100">
                      <Button
                        variant="ghost"
                        onClick={() => toggleSpotExpansion(spot.id)}
                        className="flex items-center gap-2 text-blue-700 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-md transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        <span className="font-semibold text-base">
                          {spotVisit.visitCount === 1 ? '1 visite' : `${spotVisit.visitCount} visites`}
                        </span>
                        <span className="text-sm font-normal text-blue-600">
                          - Voir le détail
                        </span>
                      </Button>
                      
                      <div className="text-sm text-gray-500">
                        {spotVisit.firstVisitDate && spotVisit.lastVisitDate && (
                          <span>
                            {spotVisit.visitCount === 1 ? (
                              `Le ${formatCompactDate(spotVisit.firstVisitDate)}`
                            ) : (
                              `Du ${formatCompactDate(spotVisit.firstVisitDate)} au ${formatCompactDate(spotVisit.lastVisitDate)}`
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Visit Details */}
                {isExpanded && (
                  <div className="space-y-4 border-t border-blue-200 pt-4 bg-gradient-to-b from-blue-25 to-white rounded-b-lg">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800 text-base mb-3 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        Historique détaillé des visites ({spotVisit.visitCount})
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleSpotExpansion(spot.id)}
                        className="text-xs"
                      >
                        Masquer
                      </Button>
                    </div>
                    
                    {spotVisit.visits.map((visit, index) => {
                      const visitDateTime = formatVisitDate(visit.visit_date);
                      
                      return (
                        <div key={`${visit.id || index}-${visit.visit_date}`} className="bg-white border-2 border-blue-100 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
                          {/* Visit Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-4 text-sm text-gray-700">
                              <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-md">
                                <Calendar className="h-4 w-4 text-blue-600" />
                                <span className="font-semibold text-blue-800">{visitDateTime.date}</span>
                              </div>
                              <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                                <Clock className="h-4 w-4 text-gray-600" />
                                <span className="font-medium">{visitDateTime.time}</span>
                              </div>
                            </div>

                            {/* Rating */}
                            {visit.rating && (
                              <div className="flex flex-col items-end gap-1">
                                {renderStars(visit.rating)}
                                <span className="text-xs text-gray-500">{visit.rating}/5</span>
                              </div>
                            )}
                          </div>

                          {/* Weather */}
                          {visit.weather && (
                            <div className="flex items-center gap-2 mb-3">
                              <Cloud className="h-4 w-4 text-blue-500" />
                              <Badge variant="outline" className="text-xs">
                                {visit.weather === 'sunny' ? 'Ensoleillé' : 
                                 visit.weather === 'partly-cloudy' ? 'Partiellement nuageux' : 
                                 visit.weather}
                              </Badge>
                            </div>
                          )}

                          {/* Description */}
                          {visit.description && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2 mb-2">
                                <MessageCircle className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">Notes de visite</span>
                              </div>
                              <p className="text-sm text-gray-700 leading-relaxed">
                                {visit.description}
                              </p>
                            </div>
                          )}

                          {/* Photos placeholder */}
                          {visit.photos && visit.photos.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <Camera className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">
                                  Photos de visite ({visit.photos.length})
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                (Photos seront affichées ici)
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}